const API_BASE_URL = (process.env.REACT_APP_API_BASE_URL || 'http://localhost:3333/api/v1').replace(/\/+$/, '');

const toQueryString = (params = {}) => {
  const search = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') {
      search.append(key, String(value));
    }
  });
  const qs = search.toString();
  return qs ? `?${qs}` : '';
};

const getStoredToken = () => {
  try {
    const raw = localStorage.getItem('tema_auth');
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return parsed?.token || null;
  } catch {
    return null;
  }
};

const normalizeResponse = (payload) => {
  if (payload && typeof payload === 'object' && 'status' in payload && 'message' in payload) {
    return payload;
  }
  return { status: true, message: 'Success', data: payload ?? null };
};

const normalizeError = (statusCode, payload) => {
  const message = payload?.message || `Request gagal (HTTP ${statusCode})`;
  const error = new Error(message);
  error.status = false;
  error.httpStatus = statusCode;
  error.data = payload?.data ?? null;
  return error;
};

const parseResponsePayload = async (response) => {
  const text = await response.text();
  let payload = null;
  if (text) {
    try {
      payload = JSON.parse(text);
    } catch {
      payload = { message: text };
    }
  }
  return payload;
};

export const request = async (path, { method = 'GET', params, body, auth = true, headers = {} } = {}) => {
  const token = auth ? getStoredToken() : null;
  const url = `${API_BASE_URL}${path.startsWith('/') ? path : `/${path}`}${toQueryString(params)}`;

  const finalHeaders = {
    ...(body instanceof FormData ? {} : { 'Content-Type': 'application/json' }),
    ...headers,
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };

  const response = await fetch(url, {
    method,
    headers: finalHeaders,
    body: body === undefined ? undefined : (body instanceof FormData ? body : JSON.stringify(body)),
  });

  const payload = await parseResponsePayload(response);

  if (!response.ok || (payload && payload.status === false)) {
    throw normalizeError(response.status, payload);
  }

  return normalizeResponse(payload);
};

export const requestBlob = async (path, { method = 'GET', params, auth = true, headers = {} } = {}) => {
  const token = auth ? getStoredToken() : null;
  const url = `${API_BASE_URL}${path.startsWith('/') ? path : `/${path}`}${toQueryString(params)}`;

  const finalHeaders = {
    ...headers,
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };

  const response = await fetch(url, {
    method,
    headers: finalHeaders,
  });

  if (!response.ok) {
    const payload = await parseResponsePayload(response);
    throw normalizeError(response.status, payload);
  }

  return {
    status: true,
    message: 'Success',
    data: await response.blob(),
    headers: response.headers,
  };
};

export const api = {
  get: (path, options = {}) => request(path, { ...options, method: 'GET' }),
  getBlob: (path, options = {}) => requestBlob(path, { ...options, method: 'GET' }),
  post: (path, body, options = {}) => request(path, { ...options, method: 'POST', body }),
  put: (path, body, options = {}) => request(path, { ...options, method: 'PUT', body }),
  delete: (path, options = {}) => request(path, { ...options, method: 'DELETE' }),
};

// ===== Pagination helpers =====
// Real API returns doubly-nested: { data: { page, limit, total, last_page, data: { perPage, lastPage, data: [...] } } }
// These helpers handle flat array / single-nested / doubly-nested transparently.
export const extractList = (resData) => {
  if (Array.isArray(resData)) return resData;                        // flat array
  if (Array.isArray(resData?.data)) return resData.data;            // { data: [...] }
  if (Array.isArray(resData?.data?.data)) return resData.data.data; // { data: { data: [...] } }
  return [];
};

export const extractMeta = (resData) => {
  if (Array.isArray(resData)) {
    return { page: 1, last_page: 1, total: resData.length };
  }
  if (Array.isArray(resData?.data)) {
    return {
      page: resData.page ?? 1,
      last_page: resData.last_page ?? resData.lastPage ?? 1,
      total: resData.total ?? resData.data.length,
    };
  }
  if (resData?.data && Array.isArray(resData.data.data)) {
    const p = resData.data;
    return {
      page: p.page ?? p.currentPage ?? 1,
      last_page: p.lastPage ?? p.last_page ?? 1,
      total: p.total ?? p.data.length,
    };
  }
  return { page: 1, last_page: 1, total: 0 };
};

